import React, { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './Dialog';
import { useApi } from '../hooks/useApi';
import { SyncProductsResponse } from '../types/api';
import { ArrowsClockwise, Warning, CheckCircle } from 'phosphor-react';
import { Loader } from './Loader';

export const ProductSyncSection: React.FC = () => {
  const api = useApi();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncProductsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOpenConfirmDialog = () => {
    setConfirmText('');
    setError(null);
    setSyncResult(null); // Clear previous result
    setIsConfirmOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setIsConfirmOpen(false);
    setConfirmText('');
    setError(null);
  };

  const handleSync = async () => {
    if (confirmText !== 'CONFIRMAR') {
      setError('Debes escribir "CONFIRMAR" exactamente para continuar');
      return;
    }

    try {
      setIsSyncing(true);
      setError(null);

      const result = await api.syncProducts('CONFIRMAR');

      setSyncResult(result);
      handleCloseConfirmDialog();
    } catch (err) {
      console.error('Sync error:', err);

      // Provide more specific error messages
      let errorMessage = 'Error al sincronizar productos';

      if (err instanceof Error) {
        const message = err.message.toLowerCase();

        // CORS error
        if (message.includes('cors') || message.includes('access-control-allow-origin')) {
          errorMessage = '❌ Error de configuración: El servidor no permite conexiones desde este dominio. Contacta al administrador del sistema.';
        }
        // Network errors (503, Failed to fetch, etc.)
        else if (message.includes('503') || message.includes('service unavailable')) {
          errorMessage = '❌ Servicio no disponible: El servidor está temporalmente fuera de servicio. Intenta nuevamente en unos minutos.';
        }
        else if (message.includes('failed to fetch') || message.includes('network')) {
          errorMessage = '❌ Error de conexión: No se puede conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente.';
        }
        // Timeout errors
        else if (message.includes('timeout')) {
          errorMessage = '❌ Tiempo de espera agotado: El servidor tardó demasiado en responder. Intenta nuevamente.';
        }
        // Use the original error message if available
        else if (err.message) {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  const isConfirmValid = confirmText === 'CONFIRMAR';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-sans font-semibold text-xl text-foreground">
          Sincronización de Productos
        </h2>
        <p className="font-sans text-sm text-text-subtle mt-1">
          Sincroniza productos desde Kameone ERP a tu base de datos local
        </p>
      </div>

      {/* Warning Card */}
      <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-6">
        <div className="flex gap-4">
          <Warning size={24} weight="fill" className="text-destructive shrink-0 mt-1" />
          <div>
            <h3 className="font-sans font-semibold text-base text-destructive mb-2">
              ⚠️ Acción Destructiva
            </h3>
            <div className="space-y-2">
              <p className="font-sans text-sm text-foreground">
                Esta acción borrará <strong>TODOS</strong> los siguientes datos de tu organización:
              </p>
              <ul className="list-disc list-inside space-y-1 font-sans text-sm text-foreground ml-2">
                <li>Productos</li>
                <li>Inventario (stock)</li>
                <li>Pedidos y artículos de pedidos</li>
                <li>Conversaciones y mensajes</li>
              </ul>
              <p className="font-sans text-sm text-foreground mt-3">
                Después del borrado, se sincronizarán los productos desde Kameone ERP.
              </p>
              <p className="font-sans text-sm font-semibold text-destructive mt-3">
                Esta operación es <strong>irreversible</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Result - Success */}
      {syncResult && syncResult.success && (
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
          <div className="flex gap-4">
            <CheckCircle size={24} weight="fill" className="text-green-600 shrink-0 mt-1" />
            <div className="w-full">
              <h3 className="font-sans font-semibold text-base text-green-800 mb-2">
                ✓ Sincronización Completada
              </h3>
              <p className="font-sans text-sm text-green-700">
                <strong>{syncResult.syncedProducts}</strong> productos sincronizados exitosamente desde Kameone.
              </p>
              {syncResult.errors && syncResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="font-sans text-sm font-semibold text-orange-700">
                    Advertencias:
                  </p>
                  <ul className="list-disc list-inside space-y-1 font-sans text-sm text-orange-600 ml-2 mt-1">
                    {syncResult.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="font-sans text-xs text-text-subtle mt-3">
                {new Date(syncResult.timestamp).toLocaleString('es-CL')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Result - Error */}
      {error && !isConfirmOpen && (
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
          <div className="flex gap-4">
            <Warning size={24} weight="fill" className="text-red-600 shrink-0 mt-1" />
            <div className="w-full">
              <h3 className="font-sans font-semibold text-base text-red-800 mb-2">
                Error en la Sincronización
              </h3>
              <p className="font-sans text-sm text-red-700 whitespace-pre-line">
                {error}
              </p>
              {(error.includes('CORS') || error.includes('configuración') || error.includes('503')) && (
                <div className="mt-3 p-3 bg-red-100 rounded border border-red-300">
                  <p className="font-sans text-xs text-red-800 font-semibold mb-1">
                    💡 Posibles soluciones:
                  </p>
                  <ul className="list-disc list-inside space-y-1 font-sans text-xs text-red-700 ml-2">
                    <li>Verifica que el backend esté ejecutándose correctamente</li>
                    <li>Confirma que el dominio esté configurado en CORS del backend</li>
                    <li>Si el error persiste, contacta al administrador del sistema</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sync Button */}
      <div className="flex items-start gap-4">
        <Button
          variant="destructive"
          onClick={handleOpenConfirmDialog}
          disabled={isSyncing}
          className="gap-2"
        >
          <ArrowsClockwise size={18} weight="bold" />
          Sincronizar Productos desde Kameone
        </Button>
      </div>

      {/* Info Box */}
      <div className="border border-border rounded-lg p-4 bg-muted/20">
        <h4 className="font-sans font-semibold text-sm text-foreground mb-2">
          ℹ️ Información
        </h4>
        <ul className="space-y-1 font-sans text-sm text-text-subtle">
          <li>• Asegúrate de tener configurada la API key de Kameone antes de sincronizar</li>
          <li>• La sincronización puede tomar varios minutos dependiendo de la cantidad de productos</li>
          <li>• Se recomienda realizar esta operación en horarios de baja actividad</li>
          <li>• Todos los datos antiguos serán borrados antes de la sincronización</li>
        </ul>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-destructive">
              Confirmar Sincronización
            </DialogTitle>
            <DialogDescription>
              Esta acción es <strong>irreversible</strong> y borrará todos los productos,
              pedidos y conversaciones actuales.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Warning in Dialog */}
            <div className="bg-destructive/10 border border-destructive rounded-md p-3">
              <p className="font-sans text-sm text-destructive font-semibold">
                ⚠️ Último aviso
              </p>
              <p className="font-sans text-xs text-foreground mt-1">
                Se eliminarán permanentemente todos los productos y pedidos de tu organización.
              </p>
            </div>

            {/* Confirmation Input */}
            <div>
              <label className="font-sans font-medium text-sm text-foreground block mb-2">
                Escribe <span className="font-mono bg-muted px-1 rounded">CONFIRMAR</span> para continuar:
              </label>
              <Input
                type="text"
                value={confirmText}
                onChange={(e) => {
                  setConfirmText(e.target.value);
                  setError(null);
                }}
                placeholder="CONFIRMAR"
                error={!!error}
                disabled={isSyncing}
                autoFocus
              />
              {error && (
                <p className="font-sans text-sm text-destructive mt-1">
                  {error}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="ghost"
              onClick={handleCloseConfirmDialog}
              disabled={isSyncing}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleSync}
              disabled={!isConfirmValid || isSyncing}
              className="gap-2"
            >
              {isSyncing ? (
                <>
                  <Loader />
                  Sincronizando...
                </>
              ) : (
                <>
                  <ArrowsClockwise size={16} weight="bold" />
                  Confirmar Sincronización
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
